// AvatarCropper — reusable square-crop modal for any profile photo
// upload across the platform (player, admin, agent, bot, and
// admin-uploads-on-behalf-of-player).
//
// Workflow:
//   1. Caller opens the modal. The component internally pops the
//      file picker (or accepts a pre-picked File via prop).
//   2. User drags / pinches / wheel-zooms the picture inside a fixed
//      square frame to choose the visible region.
//   3. On confirm, we render that region into an offscreen canvas at
//      a standard fixed resolution and export as a WebP blob at
//      controlled quality. The blob is what `onUpload` receives.
//
// Standard output: OUTPUT_SIZE × OUTPUT_SIZE WebP at quality
// QUALITY. ~10-30 KB per avatar — small enough that the network
// cost of loading 50 avatars on a list page is negligible. The
// resolution is chosen so that even a Retina display showing the
// avatar at 96px (the largest place anywhere) still has 1.6× pixel
// density to draw from.
//
// Self-contained — no external cropping library, no SVG, no extra
// CSS frameworks. Works with mouse, touch (single-finger drag,
// two-finger pinch zoom), and the scroll wheel.

import {
  PointerEvent as RPE, ReactElement, useCallback,
  useEffect, useRef, useState,
} from 'react';
import Icon from './Icon';

const DEFAULT_OUTPUT_SIZE = 320;     // pixels — default final image dimension
const DEFAULT_QUALITY     = 0.82;    // 0..1 (only used by lossy formats)
const FRAME_SIZE          = 280;     // px — visible crop frame in the modal
const MAX_SCALE           = 5;       // arbitrary upper bound

type Format = 'image/webp' | 'image/png' | 'image/jpeg';

type Props = {
  /** Called with the cropped File (mime + extension match `outputFormat`). */
  onUpload: (file: File) => Promise<void> | void;
  /** Called when the user closes the modal without uploading. */
  onClose: () => void;
  /** Modal header title. */
  title?: string;
  /** A pre-picked File. If omitted, the modal pops the file picker. */
  initialFile?: File;
  /** Final image side-length, square. Default 320. App icons pass 1024. */
  outputSize?: number;
  /** Image MIME type for the output. Default WebP; app icons want PNG. */
  outputFormat?: Format;
  /** Quality 0..1 for lossy formats. Ignored for PNG. */
  outputQuality?: number;
  /** Filename embedded in the resulting File. Default derived from format. */
  outputFilename?: string;
  /** Caption under the zoom slider (override default). */
  hint?: string;
};

const EXT_FOR: Record<Format, string> = {
  'image/webp': 'webp',
  'image/png':  'png',
  'image/jpeg': 'jpg',
};

export default function AvatarCropper({
  onUpload, onClose, title = 'Adjust photo', initialFile,
  outputSize     = DEFAULT_OUTPUT_SIZE,
  outputFormat   = 'image/webp',
  outputQuality  = DEFAULT_QUALITY,
  outputFilename,
  hint,
}: Props): ReactElement {
  const ext = EXT_FOR[outputFormat];
  const filename = outputFilename || `image.${ext}`;
  const fileRef    = useRef<HTMLInputElement>(null);
  const frameRef   = useRef<HTMLDivElement>(null);
  // The image-state — natural size + the user's transform.
  const [src,   setSrc]   = useState<string | null>(null);
  const [imgNW, setImgNW] = useState(0);            // natural width
  const [imgNH, setImgNH] = useState(0);            // natural height
  const [tx,    setTx]    = useState(0);            // translate x (px)
  const [ty,    setTy]    = useState(0);            // translate y (px)
  const [scale, setScale] = useState(1);
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState<string | null>(null);
  // Rule-of-thirds grid (composition guide). Default ON. Persisted in
  // localStorage under `cropper.grid` so a user's preference carries
  // across uploads + reloads. Falls back to ON on private-mode browsers
  // where localStorage throws.
  const [showGrid, setShowGrid] = useState<boolean>(() => {
    try { return localStorage.getItem('cropper.grid') !== 'off'; }
    catch { return true; }
  });
  function toggleGrid() {
    setShowGrid((v) => {
      const next = !v;
      try { localStorage.setItem('cropper.grid', next ? 'on' : 'off'); }
      catch { /* private mode — silently skip */ }
      return next;
    });
  }

  // Pop the picker on mount unless we got the file via prop.
  useEffect(() => {
    if (initialFile) {
      void loadFile(initialFile);
    } else {
      // Auto-open file picker once the modal has mounted.
      const id = window.setTimeout(() => fileRef.current?.click(), 30);
      return () => window.clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup the object URL on unmount / when src changes.
  useEffect(() => {
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [src]);

  // ── File handling ────────────────────────────────────────────────
  const loadFile = useCallback(async (f: File) => {
    if (!f.type.startsWith('image/')) {
      setErr('Please pick an image file (PNG / JPG / WebP / HEIC).');
      return;
    }
    setErr(null);
    // Dispose previous URL.
    if (src) URL.revokeObjectURL(src);
    const url = URL.createObjectURL(f);
    setSrc(url);
    // Load natural dimensions to compute MIN_SCALE such that the
    // image always covers the crop frame (no transparent gaps).
    const img = new Image();
    img.onload = () => {
      setImgNW(img.naturalWidth);
      setImgNH(img.naturalHeight);
      // Initial: scale just covers the frame, centered.
      const min = FRAME_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
      setScale(min);
      setTx(0);
      setTy(0);
    };
    img.onerror = () => setErr('Could not read this image. Try another file.');
    img.src = url;
  }, [src]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) loadFile(f);
  }

  // ── Pan / zoom handlers ──────────────────────────────────────────
  // Active pointers — supports two-finger pinch-zoom and one-finger pan.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);

  function clampTx(nextTx: number, nextScale: number) {
    // Keep image overlap with the frame: extreme positions only let
    // an edge of the (scaled) image touch the corresponding frame edge.
    const halfFrame = FRAME_SIZE / 2;
    const halfImgW  = (imgNW * nextScale) / 2;
    const limit = Math.max(0, halfImgW - halfFrame);
    return Math.max(-limit, Math.min(limit, nextTx));
  }
  function clampTy(nextTy: number, nextScale: number) {
    const halfFrame = FRAME_SIZE / 2;
    const halfImgH  = (imgNH * nextScale) / 2;
    const limit = Math.max(0, halfImgH - halfFrame);
    return Math.max(-limit, Math.min(limit, nextTy));
  }

  function onPointerDown(e: RPE<HTMLDivElement>) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      dragStart.current = { x: e.clientX, y: e.clientY, tx, ty };
    } else if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchStart.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        scale,
      };
    }
  }
  function onPointerMove(e: RPE<HTMLDivElement>) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setTx(clampTx(dragStart.current.tx + dx, scale));
      setTy(clampTy(dragStart.current.ty + dy, scale));
    } else if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const next = pinchStart.current.scale * (dist / pinchStart.current.dist);
      const min = FRAME_SIZE / Math.min(imgNW, imgNH);
      const clamped = Math.max(min, Math.min(MAX_SCALE * min, next));
      setScale(clamped);
      // Re-clamp translation so the new scale doesn't cause an edge gap.
      setTx((t) => clampTx(t, clamped));
      setTy((t) => clampTy(t, clamped));
    }
  }
  function onPointerUp(e: RPE<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  }
  function onWheel(e: React.WheelEvent<HTMLDivElement>) {
    e.preventDefault();
    const min = FRAME_SIZE / Math.min(imgNW, imgNH);
    const factor = e.deltaY < 0 ? 1.06 : 1 / 1.06;
    const next = scale * factor;
    const clamped = Math.max(min, Math.min(MAX_SCALE * min, next));
    setScale(clamped);
    setTx((t) => clampTx(t, clamped));
    setTy((t) => clampTy(t, clamped));
  }

  // ── Render output → blob ─────────────────────────────────────────
  async function confirm() {
    if (!src || !imgNW || !imgNH) return;
    setBusy(true); setErr(null);
    try {
      // The user has positioned the image such that:
      //   image_natural * scale, translated by (tx, ty),
      //   centered in a FRAME_SIZE square.
      //
      // To extract the cropped region we:
      //   1. Compute the source rectangle in the *natural* image
      //      that maps to the (FRAME_SIZE × FRAME_SIZE) frame.
      //   2. drawImage that region onto an OUTPUT_SIZE canvas.
      const img = new Image();
      const blob = await new Promise<Blob>((resolve, reject) => {
        img.onload = () => {
          // Source side length in natural pixels.
          const srcSide = FRAME_SIZE / scale;
          // Center of the visible frame in image-natural coords.
          // (When tx=ty=0 the image center sits at the frame center.)
          const centerX = imgNW / 2 - tx / scale;
          const centerY = imgNH / 2 - ty / scale;
          const sx = Math.max(0, Math.min(imgNW - srcSide, centerX - srcSide / 2));
          const sy = Math.max(0, Math.min(imgNH - srcSide, centerY - srcSide / 2));
          const c = document.createElement('canvas');
          c.width = outputSize; c.height = outputSize;
          const ctx = c.getContext('2d');
          if (!ctx) return reject(new Error('Canvas context missing'));
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, sx, sy, srcSide, srcSide, 0, 0, outputSize, outputSize);
          c.toBlob(
            (b) => b ? resolve(b) : reject(new Error('toBlob failed')),
            outputFormat,
            // PNG ignores the quality arg; WebP / JPEG use it.
            outputFormat === 'image/png' ? undefined : outputQuality,
          );
        };
        img.onerror = () => reject(new Error('image load failed'));
        img.src = src;
      });
      const file = new File([blob], filename, { type: outputFormat });
      await onUpload(file);
      onClose();
    } catch (e) {
      setErr((e as Error).message || 'Could not save photo');
      setBusy(false);
    }
  }

  // ── UI ───────────────────────────────────────────────────────────
  const transform = `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-neutral-900 border border-neutral-700 shadow-2xl overflow-hidden">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-neutral-800">
          <Icon name="crop_square" size={20} className="text-emerald-300" />
          <h2 className="text-base font-bold text-neutral-100 flex-1">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 -mr-1 rounded-full hover:bg-white/10"
            aria-label="Close"
          >
            <Icon name="close" size={20} />
          </button>
        </header>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
        />

        {/* Crop frame */}
        <div
          className="relative overflow-hidden bg-neutral-950 mx-auto"
          style={{
            width: FRAME_SIZE, height: FRAME_SIZE,
            touchAction: 'none', userSelect: 'none',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          ref={frameRef}
        >
          {!src && (
            <div className="absolute inset-0 grid place-items-center text-center text-neutral-500 text-sm px-4">
              <div>
                <Icon name="add_photo_alternate" size={36} className="text-neutral-600 mb-2" />
                <p>Pick an image to start.</p>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold"
                >
                  <Icon name="upload" size={14} /> Choose file
                </button>
              </div>
            </div>
          )}
          {src && (
            <img
              src={src}
              alt=""
              draggable={false}
              className="absolute top-1/2 left-1/2 max-w-none pointer-events-none select-none"
              style={{
                width:  imgNW || 'auto',
                height: imgNH || 'auto',
                transform,
                transformOrigin: 'center center',
              }}
            />
          )}
          {/* Rule-of-thirds composition grid. Two vertical + two
              horizontal lines split the frame into 9 equal cells; the
              lines use mix-blend-difference so they stay visible
              against any photo (white over light → dark, white over
              dark → bright). Pointer-events-none so the underlying
              drag/pan handlers still work. */}
          {src && showGrid && (
            <div className="pointer-events-none absolute inset-0 mix-blend-difference">
              {/* Vertical lines at 1/3 and 2/3 */}
              <div className="absolute top-0 bottom-0 left-1/3 w-px bg-white/85" />
              <div className="absolute top-0 bottom-0 left-2/3 w-px bg-white/85" />
              {/* Horizontal lines at 1/3 and 2/3 */}
              <div className="absolute left-0 right-0 top-1/3 h-px bg-white/85" />
              <div className="absolute left-0 right-0 top-2/3 h-px bg-white/85" />
              {/* Centre tick — small + glyph at exact frame centre,
                  helps when aligning a face or symmetric subject. */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                              w-3 h-3">
                <div className="absolute top-1/2 left-0 right-0 h-px bg-white/85" />
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/85" />
              </div>
            </div>
          )}

          {/* Grid on/off toggle — floating chip in the top-right of
              the frame. Only shown once an image is loaded (no point
              before). Pointer-events-auto so it's clickable; the
              parent frame's pointer handlers ignore it because the
              click never falls through. */}
          {src && (
            <button
              onClick={toggleGrid}
              type="button"
              title={showGrid ? 'Hide composition grid' : 'Show composition grid'}
              className={`absolute top-2 right-2 z-10 inline-flex items-center gap-1
                          rounded-full px-2 py-1 text-[10.5px] font-semibold
                          backdrop-blur-sm transition
                          ${showGrid
                            ? 'bg-emerald-500/85 text-white hover:bg-emerald-500'
                            : 'bg-black/55 text-neutral-200 hover:bg-black/75 ring-1 ring-white/15'}`}
            >
              <Icon name={showGrid ? 'grid_on' : 'grid_off'} size={13} />
              <span>Grid</span>
            </button>
          )}

          {/* Soft corner overlay — visualises the square crop region. */}
          <div className="pointer-events-none absolute inset-0 ring-2 ring-emerald-400/70 ring-inset rounded-md" />
          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.0)]" />
        </div>

        {/* Zoom slider */}
        {src && (
          <div className="px-4 pt-3 pb-1">
            <label className="block text-[11px] uppercase tracking-wider text-neutral-400 mb-1">Zoom</label>
            <input
              type="range"
              min={FRAME_SIZE / Math.min(imgNW || FRAME_SIZE, imgNH || FRAME_SIZE)}
              max={(FRAME_SIZE / Math.min(imgNW || FRAME_SIZE, imgNH || FRAME_SIZE)) * MAX_SCALE}
              step="0.001"
              value={scale}
              onChange={(e) => {
                const next = Number(e.target.value);
                setScale(next);
                setTx((t) => clampTx(t, next));
                setTy((t) => clampTy(t, next));
              }}
              className="w-full accent-emerald-500"
            />
            <p className="text-[10.5px] text-neutral-500 mt-1">
              {hint || (
                <>Drag to reposition · pinch / scroll / use slider to zoom · final size {outputSize}×{outputSize} {ext.toUpperCase()}</>
              )}
            </p>
          </div>
        )}

        {err && (
          <div className="mx-4 mt-2 mb-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-xs px-3 py-2">
            {err}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-neutral-800 bg-neutral-950/40">
          <button
            onClick={() => fileRef.current?.click()}
            className="px-3 py-2 rounded-xl text-xs font-semibold text-neutral-300
                       bg-white/5 border border-white/10 hover:bg-white/10
                       inline-flex items-center gap-1"
          >
            <Icon name="cached" size={14} /> Change file
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm bg-white/5 border border-white/10 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!src || busy}
            className="px-4 py-2 rounded-xl text-sm font-bold text-white
                       bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed
                       inline-flex items-center gap-1.5"
          >
            {busy
              ? <Icon name="progress_activity" size={16} className="animate-spin" />
              : <Icon name="check" size={16} />}
            {busy ? 'Uploading…' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
